import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, RefreshCw, ServerCog } from "lucide-react";
import { api } from "../lib/api";

/**
 * Server-status pills — one-shot admin diagnostic that reads `/api/healthz`
 * feature flags and renders green/red pills. Ideal for the "why is X not
 * working on my self-hosted box?" support flow: a single glance tells the
 * operator which optional deps and endpoints are missing.
 */
const LABELS = {
  csv_import_endpoint: "CSV import endpoint",
  excel_xlsx_support:  "Excel .xlsx parser (openpyxl)",
  excel_xls_support:   "Excel .xls parser (xlrd)",
  emergentintegrations: "LLM proxy (emergentintegrations)",
  weasyprint:          "PDF renderer (WeasyPrint)",
  meteronline_backfill_endpoint: "MeterOnline backfill endpoint",
  solis_client:        "Solis client",
  solis_test_endpoint: "Solis test endpoint",
};

export default function ServerStatusSection() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/healthz");
      setHealth(data);
    } catch (e) {
      setHealth({ ok: false, features: {} });
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <section className="swiss-card-flat p-6" data-testid="server-status-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label flex items-center gap-2">
            <ServerCog size={12} /> Server status
          </div>
          <div className="font-display text-lg font-semibold mt-1">Runtime capabilities</div>
        </div>
        <div className="flex items-center gap-2">
          {health && (
            <span className={`tag text-[10px] ${health.ok ? "ok" : "warn"}`}
                  data-testid="server-db-status">
              {health.ok ? "DB reachable" : "DB unreachable"}
            </span>
          )}
          <button onClick={refresh} disabled={loading}
                  className="btn-ghost text-[10px] disabled:opacity-50"
                  data-testid="server-status-refresh-btn">
            <RefreshCw size={11} /> {loading ? "Checking…" : "Re-check"}
          </button>
        </div>
      </div>

      {!health ? (
        <div className="text-xs muted">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.entries(health.features || {}).map(([key, ok]) => (
            <Pill key={key} label={LABELS[key] || key} ok={!!ok} testid={`server-status-${key}`} />
          ))}
        </div>
      )}

      <p className="text-[11px] muted mt-3">
        Reads from <code className="font-mono">/api/healthz</code>. Reflects the running backend process — if you just re-ran <code>install.sh</code>, hit Re-check.
      </p>
    </section>
  );
}

function Pill({ label, ok, testid }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 border text-xs
                     ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border-red-200 bg-red-50 text-red-800"}`}
         data-testid={testid}>
      {ok ? <CheckCircle2 size={13} className="text-emerald-600" />
          : <XCircle size={13} className="text-red-600" />}
      <span className="flex-1">{label}</span>
      <span className={`font-mono text-[10px] uppercase ${ok ? "text-emerald-700" : "text-red-700"}`}>
        {ok ? "ON" : "OFF"}
      </span>
    </div>
  );
}
